import type { BatchOnboardRequest, PortfolioEngineAccount } from '@ndvr/portfolio-api';

import type { PortfolioInferredPositions } from '@/cip/_openapi/portfolio-optimizer';
import { formatWeightForSave } from '@/views/portfolio-engine/allocation-builder/shared-utils';

import { effectiveReplacements, TLHHolding, TLHReplacement } from './api/tlh-types';
import { OnboardingWizardState, TaxLossHarvestingSettings } from './types';
import { positionKey } from './utils';

interface BuildArgs {
    accounts: PortfolioEngineAccount[];
    state: OnboardingWizardState;
    inferred: PortfolioInferredPositions[];
    tlhHoldings: TLHHolding[];
}

export const buildOnboardSubmission = ({ accounts, state, inferred, tlhHoldings }: BuildArgs): BatchOnboardRequest => {
    const inferredByGuid = new Map(inferred.map((p) => [p.portfolioGuid, p]));
    return {
        tlh: state.management.taxLossHarvesting,
        cashEq: state.management.cashEqSweep,
        accounts: accounts.map((account) => {
            const positions = inferredByGuid.get(account.accountGUID)?.positions ?? [];
            const overrides = state.weightOverrides[account.accountGUID] ?? {};
            const included = positions.filter((p) => parseFloat(overrides[positionKey(p)] ?? '0') > 0);
            const weights = included.map((p) => parseFloat(formatWeightForSave(overrides[positionKey(p)])));
            return {
                accountGUID: account.accountGUID,
                assetAllocations: included.map((p, i) => ({
                    securityId: p.sId,
                    shareClassId: requireShareClassId(p),
                    weight: String(weights[i]),
                    replacementAssets: [],
                })),
            };
        }),
        replacements: state.management.taxLossHarvesting
            ? buildReplacementSubmissions(tlhHoldings, state.taxLossHarvesting)
                  .filter((entry) => entry.replacements.length > 0)
                  .map((entry) => ({
                      securityId: entry.holdingSId,
                      replacementAssets: entry.replacements.map((r) => ({ securityId: r.sId, shareClassId: r.scId })),
                  }))
            : [],
    };
};

const requireShareClassId = ({ sId, scId }: { sId: string; scId?: string }): string => {
    if (!scId) {
        throw new Error(`Security ${sId} has no share class id and cannot be used here`);
    }
    return scId;
};

const buildReplacementSubmissions = (
    holdings: TLHHolding[],
    settings: TaxLossHarvestingSettings,
): { holdingSId: string; replacements: TLHReplacement[] }[] =>
    holdings.map((holding) => ({
        holdingSId: holding.sId,
        replacements: effectiveReplacements(holding, settings.replacementsByHolding),
    }));
